import axios from 'axios';
import { auth } from '../screens/firebaseConfig';
import { apiUrl } from '../screens/utils/api';

/**
 * Attaches the signed-in user's Firebase ID token to every request this app makes to our own API.
 *
 * Why this exists: the app has always authenticated against Firebase, but it then told the backend
 * who it was with a plain `X-Tenant-User: <email>` header — a claim the API had no way to check.
 * Anyone with curl could read or write any tenant's data by naming a user's email. The token is the
 * proof that was missing; `getIdToken()` returns the JWT Firebase issued, which the backend verifies
 * by signature.
 *
 * Three details that matter:
 *
 *  - **Only our API.** The token is scoped to requests whose URL is our backend. A global axios
 *    default would leak a bearer credential to any third-party host a future feature calls.
 *  - **Refresh is automatic.** `getIdToken()` returns the cached token until it is close to expiry
 *    (Firebase tokens last an hour) and silently refreshes when it isn't. Reading it per request is
 *    therefore cheap, and always current — caching it ourselves would reintroduce the expiry bug.
 *  - **A missing token is not a failure.** The request still goes out. The backend runs in
 *    `permissive` mode during the transition and falls back to the legacy header, so a signed-out
 *    caller or a token hiccup degrades to today's behaviour instead of breaking the screen.
 */

let installed = false;

/** True when the request is going to our own backend rather than some third party. */
function isOwnApi(url) {
  if (!url) return false;
  try {
    // Resolves relative URLs against the current origin, so both `/api/x` and an absolute
    // `https://…/api/x` are judged the same way.
    const target = new URL(url, window.location.origin);
    const api = new URL(apiUrl, window.location.origin);
    return target.origin === api.origin;
  } catch (e) {
    return false;
  }
}

export function installIdTokenInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use(async (config) => {
    const url = config.baseURL ? `${config.baseURL}${config.url || ''}` : config.url;
    if (!isOwnApi(url)) return config;

    const user = auth.currentUser;
    if (!user) return config;

    try {
      const token = await user.getIdToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      // Network blip while refreshing, or a revoked session. Let the request proceed unauthenticated
      // rather than throwing here — an interceptor that rejects turns one expired token into a
      // screenful of failures the user can't act on.
    }
    return config;
  });
}

/**
 * Force a token refresh — for the case where the backend says the token is stale but Firebase still
 * has a session (clock skew, or a token issued before a claims change).
 */
export async function refreshIdToken() {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken(true);
  } catch (e) {
    return null;
  }
}
